import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { toBlob, toSvg } from "html-to-image";
import "./WalmartAnalysis.scss";

// ── "Converge Checkout" collection assets (assets/analysis) ───────────
import bg1 from "../../assets/analysis/Bg1.png"; // intro card
import btn1 from "../../assets/analysis/Btn1.png"; // "Let's Go" pulsing button
import bg2 from "../../assets/analysis/Bg2.png";
import bg3 from "../../assets/analysis/Bg3.png";
import bg4_1 from "../../assets/analysis/Bg4-2.png";
import bg5_1 from "../../assets/analysis/Bg5-1.png";
import bg6 from "../../assets/analysis/Bg6.png";
import Logo from "../../assets/images/brands/converge.png";
import everydaySansUrl from "../../assets/fonts/EverydaySans-Medium.otf";

// Quiz definitions — question counts per quiz drive the personality
// percentage math. Adjust this import path to wherever quizData.js lives.
import { quizData } from "../Quiz/quizData";

// ── Unlock time ──────────────────────────────────────────────────────────
// Until 30 Sep 2026, 4:00 PM IST the page shows only a "come back after 4pm"
// message. No card is shown and the summary isn't fetched yet. At the unlock
// time it switches to the full Converge Checkout on its own, with no reload.
// The "+05:30" pins this to IST no matter what timezone the device is set to.
//
// 🔧 TO TEST: change the date/time below, then reload the page.
//   • A time a couple of minutes from now, e.g. "2026-09-29T22:35:00+05:30",
//     lets you watch the message switch to the checkout live.
//   • Any time already in the past, e.g. "2026-09-29T21:00:00+05:30", shows
//     the checkout immediately (what users will see after 4 PM).
//   ⚠️ Put it back to "2026-09-30T16:00:00+05:30" before deploying.
const CHECKOUT_OPENS_AT = new Date("2026-09-30T16:00:00+05:30").getTime();

const SLIDE_BACKGROUNDS = {
  interested: bg2,
  personality: bg3,
  leaderboard: bg4_1,
  crowd: bg5_1,
  hero: bg6,
};
const ALL_SLIDE_KEYS = ["interested", "personality", "leaderboard", "crowd", "hero"];

// ── Carousel / swipe tuning ──────────────────────────────────────────────
const SWIPE_THRESHOLD_PX = 50; // how far a finger must travel to change slide
const DRAG_LOCK_PX = 8; // movement needed before we decide horizontal vs vertical
const EDGE_RESISTANCE = 0.3; // rubber-band factor when dragging past first/last slide

// ── Brand font ───────────────────────────────────────────────────────────
// The page waits (briefly) for these faces before showing any dynamic text,
// so names/scores never flash in a fallback font.
const FONT_FAMILY = "Everyday Sans";
const FONT_WEIGHTS = [500]; // one file (EverydaySans-Medium.otf) covers every weight
const FONT_WAIT_TIMEOUT_MS = 3000;

// For the downloaded image we embed ONLY Everyday Sans, built from our own
// font file. Letting html-to-image scan the page instead makes it crawl every
// stylesheet in the app (boxicons, Font Awesome, Google Fonts…), which throws
// cross-origin errors and bloats the render until the browser can't draw it.
let brandFontCssPromise = null;
function getBrandFontEmbedCSS() {
  if (!brandFontCssPromise) {
    brandFontCssPromise = fetch(everydaySansUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Font HTTP ${res.status}`);
        return res.blob();
      })
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      )
      .then((dataUrl) => {
        const base64 = String(dataUrl).split(",")[1];
        return `@font-face{font-family:"${FONT_FAMILY}";src:url(data:font/otf;base64,${base64}) format("opentype");font-weight:100 900;font-style:normal;}`;
      })
      .catch((err) => {
        // Still download the card (with a fallback font) rather than fail.
        console.warn("Couldn't embed Everyday Sans in the download", err);
        brandFontCssPromise = null;
        return "";
      });
  }
  return brandFontCssPromise;
}

// Slide backgrounds are large design exports. Embedding them at full size makes
// the capture too big for Firefox (the "error { target: img }" failure), so for
// the download each one is redrawn at just the size the 1080px export needs.
// It stays PNG so any transparency survives, and is cached per image.
const exportBgCache = {};
function getExportBackground(src, outW, outH) {
  if (!exportBgCache[src]) {
    exportBgCache[src] = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          const scale = Math.min(1, Math.max(outW / img.naturalWidth, outH / img.naturalHeight));
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(img.naturalWidth * scale);
          canvas.height = Math.round(img.naturalHeight * scale);
          const ctx = canvas.getContext("2d");
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/png"));
        } catch (e) {
          resolve(src); // fall back to the original file
        }
      };
      img.onerror = () => resolve(src);
      img.src = src;
    });
  }
  return exportBgCache[src];
}

// 1×1 transparent PNG: if any image inside the card can't be inlined, it's
// swapped for this instead of aborting the whole download.
const TRANSPARENT_PX =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

// ── Download ─────────────────────────────────────────────────────────────
// 1080×1350 = 4:5 portrait, matching --card-ratio and the size Instagram /
// LinkedIn expect. Rendering at a fixed output width (instead of a fixed 2x)
// keeps the export sharp on every screen size.
const EXPORT_WIDTH = 1080;
const DOWNLOAD_RESET_MS = 2200;
const DOWNLOAD_LABELS = {
  idle: "Download",
  working: "Preparing…",
  done: "Downloaded ✓",
  error: "Couldn't download. Tap to retry",
};

// Safari's first html-to-image pass sometimes drops background images; a
// throwaway render first makes the real one reliable.
const IS_SAFARI =
  typeof navigator !== "undefined" &&
  /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);

// ── Quiz percentage math (personality slide) ─────────────────────────────
// Every quiz that EXISTS counts toward the denominator, whether or not the
// user played it. Score → correct-count is an estimate: +5/correct, -1/wrong,
// 0/unanswered means we can't recover exact correct-count from a bare score,
// so we approximate round(score/5), clamped to that quiz's question count.
const QUIZ_QUESTION_COUNTS = quizData.reduce((map, q) => {
  map[q.gname] = q.questionCount || (q.questions ? q.questions.length : 0);
  return map;
}, {});
const TOTAL_QUIZ_QUESTIONS = Object.values(QUIZ_QUESTION_COUNTS).reduce(
  (sum, n) => sum + n,
  0
);

function estimateQuizPercent(quizScores = []) {
  let correct = 0;
  quizScores.forEach(({ gname, score }) => {
    const qCount = QUIZ_QUESTION_COUNTS[gname];
    if (!qCount) return;
    correct += Math.min(qCount, Math.max(0, Math.round(score / 5)));
  });
  if (!TOTAL_QUIZ_QUESTIONS) return 0;
  return Math.round((correct / TOTAL_QUIZ_QUESTIONS) * 100);
}

// ── Personality matcher ──────────────────────────────────────────────────
// If nothing matches, return null and the caller drops the slide entirely.
function matchPersonality({ demo, morning, afternoon, evening } = {}, quizPct, firstName) {
  const name = firstName || "You";
  const allSessions = morning && afternoon && evening;
  const noSessions = !morning && !afternoon && !evening;
  const sessionCount = [morning, afternoon, evening].filter(Boolean).length;

  if (demo && allSessions) {
    return quizPct >= 80
      ? {
          article: "a",
          title: "Converge All-Star",
          subhead: `Sessions, demos, quizzes and more – you knocked everything out of the park!\nYour Converge was one for the history books.\n\nHats off, ${name}!`,
        }
      : {
          article: "an",
          title: "Omnishopper",
          subhead: `Sessions, demos, quizzes and more – you put the whole conference in your cart without missing a beat.\nThis is your receipt to show for it, ${name}!`,
        };
  }

  if (quizPct >= 80) {
    return {
      article: "a",
      title: "Quiz Whiz",
      subhead: `Quizzes appeared. You made them disappear.\nAnd getting ${quizPct}% questions right? Magical.`,
    };
  }

  if (demo && noSessions) {
    return {
      article: "a",
      title: "Demo Demolisher",
      subhead: `Seeing ≠ believing.\nYour sole focus was to dive in first-hand and put tech to the test.`,
    };
  }

  if (!demo && allSessions) {
    return {
      article: "a",
      title: "Session Superstar",
      subhead: `The agenda today was stacked – but you locked in.\nAnd you're leaving fully loaded.`,
    };
  }

  if (!demo && sessionCount <= 2) {
    return {
      article: "a",
      title: "Session Sniper",
      subhead: `You knew exactly what you came for.\nZero FOMO and that laser sharp focus paid off.`,
    };
  }

  return null;
}

// ── Leaderboard tier copy ────────────────────────────────────────────────
// `leaderboardTier` ("a" | "b" | "c" | null) comes straight from the
// backend, which already knows total participants + rank + tiebreaks.
const LEADERBOARD_COPY = {
  a: {
    headline: (name) => <>{name}, you were on 🔥 today.</>,
    subheadTail: "Incredible!\nDon't forget to collect your prizes!",
  },
  b: {
    headline: (name) => <>{name}, you just earned bragging rights.</>,
    subheadTail: "Game on!",
  },
  c: {
    headline: (name) => <>Not bad, {name}!</>,
    subheadTail: "",
  },
};

const API_BASE =
  window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
    ? "http://localhost:8080"
    : "https://walmart26-platform-admin-b19344e0cab5.herokuapp.com";

function getLoggedInEmail() {
  try {
    const raw = localStorage.getItem("authUser");
    if (!raw) return null;
    const user = JSON.parse(raw);
    return user.EmailAddress || user.Email || user.email || null;
  } catch (e) {
    return null;
  }
}

// Resolves once the brand font faces are loaded (or after a timeout, so a
// missing font file can never block the page).
function waitForBrandFont() {
  const fontSet = typeof document !== "undefined" ? document.fonts : null;
  if (!fontSet || typeof fontSet.load !== "function") return Promise.resolve();
  const loads = Promise.all(
    FONT_WEIGHTS.map((w) => fontSet.load(`${w} 16px "${FONT_FAMILY}"`).catch(() => null))
  );
  const timeout = new Promise((resolve) => setTimeout(resolve, FONT_WAIT_TIMEOUT_MS));
  return Promise.race([loads, timeout]);
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function nextFrame() {
  return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
}

// Countdown text for the locked screen, e.g. 17:28:05. Hours aren't capped at
// 24, so a 30-hour wait shows as 30:00:00 rather than rolling over.
function splitCountdown(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const pad = (n) => String(n).padStart(2, "0");
  return {
    h: pad(Math.floor(total / 3600)),
    m: pad(Math.floor((total % 3600) / 60)),
    s: pad(total % 60),
  };
}

function formatRhythmTime(isoString) {
  const d = new Date(isoString);
  let h = d.getHours() % 12;
  if (h === 0) h = 12;
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

// Small inline icons for the outro slide's social links.
const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.11 1 2.48 1s2.5 1.12 2.5 2.5zM.22 8.75h4.5V23h-4.5V8.75zM8.5 8.75h4.31v1.95h.06c.6-1.14 2.07-2.34 4.26-2.34 4.56 0 5.4 3 5.4 6.9V23h-4.5v-6.9c0-1.65-.03-3.77-2.3-3.77-2.3 0-2.65 1.8-2.65 3.65V23H8.5V8.75z" />
  </svg>
);

const InstagramIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    aria-hidden="true"
  >
    <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" />
    <circle cx="12" cy="12" r="4.2" />
    <circle cx="17.4" cy="6.6" r="0.9" fill="currentColor" stroke="none" />
  </svg>
);

// Header + footer shared by every state of the page.
const PageShell = ({ children }) => (
  <div className="walmart-wrapped-page">
    <div className="feedback-page-header">
      <button className="fb-back-link" type="button" aria-label="Back">
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="white" viewBox="0 0 24 24">
          <path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8"></path>
          <path d="m12.71 8.71-1.42-1.42L6.59 12l4.7 4.71 1.42-1.42-2.3-2.29H17v-2h-6.59z"></path>
        </svg>
      </button>
      <span className="fb-page-title">Converge Checkout</span>
    </div>
    {children}
    <div className="feedback-page-footer">
      <span className="fb-page-title">Walmart</span>
      <img src={Logo} alt="Converge" />
    </div>
  </div>
);

const WalmartAnalysis = () => {
  // True until CHECKOUT_OPENS_AT. Re-checked every second so the page opens
  // right at 4 PM, even if it was already open. msLeft drives the live
  // Hour : Min : Sec countdown on the locked screen.
  const [isLocked, setIsLocked] = useState(() => Date.now() < CHECKOUT_OPENS_AT);
  const [msLeft, setMsLeft] = useState(() => CHECKOUT_OPENS_AT - Date.now());

  useEffect(() => {
    if (!isLocked) return undefined;
    const tick = () => {
      const left = CHECKOUT_OPENS_AT - Date.now();
      setMsLeft(left);
      if (left <= 0) setIsLocked(false);
    };
    tick();
    // Ticking every 250ms (not 1000ms) keeps the seconds changing smoothly
    // without skipping one now and then because of timer drift.
    const t = setInterval(tick, 250);
    return () => clearInterval(t);
  }, [isLocked]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [phase, setPhase] = useState("intro"); // "intro" | "story"
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Live drag offset so the slide follows the finger, Instagram-style.
  const [dragPx, setDragPx] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const [downloadState, setDownloadState] = useState("idle"); // idle | working | done | error

  const cardRef = useRef(null);
  const touch = useRef({ x: 0, y: 0, dx: 0, axis: null });
  const suppressTap = useRef(false);
  const isDownloading = useRef(false);
  const downloadResetTimer = useRef(null);

  // ── Load summary + brand font together ────────────────────────────────
  useEffect(() => {
    // Don't fetch until the page unlocks. That way the summary is fetched after
    // 4 PM and includes everything up to the cutoff.
    if (isLocked) return undefined;

    const email = getLoggedInEmail();
    if (!email) {
      setError("We couldn't find your login details. Please log in again.");
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const summary = fetch(`${API_BASE}/walmartAnalysis/summary/${encodeURIComponent(email)}`, {
      signal: controller.signal,
    }).then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    });

    Promise.all([summary, waitForBrandFont()])
      .then(([json]) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error(err);
        setError("Couldn't load your Converge story. Please try again.");
        setLoading(false);
      });

    return () => controller.abort();
  }, [isLocked]);

  useEffect(() => () => clearTimeout(downloadResetTimer.current), []);

  // ── Which slides actually exist ────────────────────────────────────────
  const interestedSessions = useMemo(
    () =>
      (data?.interestedSessions || [])
        .filter((s) => s && typeof s.title === "string" && s.title.trim() !== "")
        .slice(0, 3),
    [data]
  );

  const personality = useMemo(() => {
    if (!data) return null;
    return matchPersonality(data.checkedIn, estimateQuizPercent(data.quizScores), data.firstName);
  }, [data]);

  const leaderboardCopy = useMemo(() => {
    if (!data || !data.leaderboardTier) return null;
    return LEADERBOARD_COPY[data.leaderboardTier] || null;
  }, [data]);

  const activeSlides = useMemo(
    () =>
      ALL_SLIDE_KEYS.filter((k) => {
        if (k === "interested") return interestedSessions.length > 0;
        if (k === "personality") return !!personality;
        if (k === "leaderboard") return !!leaderboardCopy;
        if (k === "crowd") return !!(data?.rhythm && data.rhythm.length > 0);
        return true;
      }),
    [interestedSessions, personality, leaderboardCopy, data]
  );
  const totalSlides = activeSlides.length;
  const isFirst = currentSlide === 0;
  const isLast = currentSlide === totalSlides - 1;

  // ── Navigation (user-controlled only — no auto-advance) ───────────────
  const goToSlide = useCallback(
    (index) => {
      if (index < 0 || index >= totalSlides) return;
      setCurrentSlide(index);
      setHasInteracted(true);
    },
    [totalSlides]
  );
  const nextSlide = useCallback(() => goToSlide(currentSlide + 1), [currentSlide, goToSlide]);
  const prevSlide = useCallback(() => goToSlide(currentSlide - 1), [currentSlide, goToSlide]);

  const handleStart = useCallback(() => {
    getBrandFontEmbedCSS(); // warm up so the first download is quick
    setPhase("story");
    setCurrentSlide(0);
  }, []);

  // Keyboard arrows on desktop.
  useEffect(() => {
    if (phase !== "story") return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") nextSlide();
      else if (e.key === "ArrowLeft") prevSlide();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, nextSlide, prevSlide]);

  // Invisible left / right tap zones.
  const onZoneTap = (dir) => () => {
    if (suppressTap.current) return;
    if (dir === "prev") prevSlide();
    else nextSlide();
  };

  // ── Touch: the track follows the finger, then snaps ───────────────────
  const resetDrag = () => {
    touch.current.axis = null;
    touch.current.dx = 0;
    setIsDragging(false);
    setDragPx(0);
  };

  const onTouchStart = (e) => {
    if (e.touches.length > 1) return;
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, dx: 0, axis: null };
  };

  const onTouchMove = (e) => {
    const t = e.touches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;

    if (!touch.current.axis) {
      if (Math.abs(dx) < DRAG_LOCK_PX && Math.abs(dy) < DRAG_LOCK_PX) return;
      touch.current.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (touch.current.axis === "x") setIsDragging(true);
    }
    if (touch.current.axis !== "x") return; // vertical → let the page scroll

    const pastEdge = (isFirst && dx > 0) || (isLast && dx < 0);
    touch.current.dx = dx;
    setDragPx(pastEdge ? dx * EDGE_RESISTANCE : dx);
  };

  const onTouchEnd = () => {
    if (touch.current.axis === "x") {
      // A swipe can be followed by a synthetic click; ignore it.
      suppressTap.current = true;
      setTimeout(() => {
        suppressTap.current = false;
      }, 350);
      const { dx } = touch.current;
      if (dx <= -SWIPE_THRESHOLD_PX) nextSlide();
      else if (dx >= SWIPE_THRESHOLD_PX) prevSlide();
    }
    resetDrag();
  };

  // ── Download ──────────────────────────────────────────────────────────
  const handleDownload = useCallback(async () => {
    const frame = cardRef.current;
    const key = activeSlides[currentSlide];
    // Capture only the slide on screen, not the whole strip of slides.
    const node = frame && frame.querySelector(`[data-slide-index="${currentSlide}"]`);
    if (!node || !key || isDownloading.current) return;
    isDownloading.current = true;
    clearTimeout(downloadResetTimer.current);
    setDownloadState("working");

    // Let "Preparing…" paint before the heavy capture work starts.
    await nextFrame();

    let result = "done";
    let options = null;
    try {
      if (document.fonts && document.fonts.ready) await document.fonts.ready;

      const width = frame.clientWidth;
      const height = frame.clientHeight;
      const exportHeight = Math.round((EXPORT_WIDTH * height) / width);

      const [fontEmbedCSS, background] = await Promise.all([
        // Always a string, so html-to-image never scans the page's stylesheets.
        getBrandFontEmbedCSS(),
        getExportBackground(SLIDE_BACKGROUNDS[key], EXPORT_WIDTH, exportHeight),
      ]);

      options = {
        width,
        height,
        pixelRatio: EXPORT_WIDTH / width,
        fontEmbedCSS,
        imagePlaceholder: TRANSPARENT_PX,
        filter: (n) => !(n.classList && n.classList.contains("ww-no-capture")),
        // Detach the slide from the carousel: fixed size, no offset, square
        // corners, and the lighter background image.
        style: {
          width: `${width}px`,
          height: `${height}px`,
          flex: "none",
          margin: "0",
          transform: "none",
          borderRadius: "0",
          boxShadow: "none",
          backgroundImage: `url("${background}")`,
        },
      };

      if (IS_SAFARI) await toBlob(node, options);
      const blob = await toBlob(node, options);
      if (!blob) throw new Error("Empty image");

      saveBlob(blob, `converge-checkout-${key}.png`);
    } catch (err) {
      console.error("Card download failed", err);
      // Diagnostic: how big the capture was (Firefox rejects very large ones).
      if (options) {
        toSvg(node, options)
          .then((svg) => console.warn(`Card capture size: ${(svg.length / 1048576).toFixed(1)} MB`))
          .catch(() => {});
      }
      result = "error";
    }

    isDownloading.current = false;
    setDownloadState(result);
    downloadResetTimer.current = setTimeout(() => setDownloadState("idle"), DOWNLOAD_RESET_MS);
  }, [activeSlides, currentSlide]);

  const renderDownloadButton = () => (
    <button
      className={`ww-download-btn is-${downloadState}`}
      onClick={handleDownload}
      type="button"
      disabled={downloadState === "working"}
      aria-busy={downloadState === "working"}
    >
      {downloadState === "working" && <span className="ww-btn-spinner" aria-hidden="true" />}
      <span aria-live="polite">{DOWNLOAD_LABELS[downloadState]}</span>
    </button>
  );

  // ── LOCKED (before 4 PM IST, 30 Sep) ──────────────────────────────────
  // No card at all — just the message.
  if (isLocked) {
    return (
      <PageShell>
        <div className="ww-locked">
          <span className="ww-locked-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
          </span>
          <p className="ww-locked-title">See your checkout journey after 4pm</p>

          {(() => {
            const { h, m, s } = splitCountdown(msLeft);
            return (
              <div
                className="ww-countdown"
                aria-label={`${Number(h)} hours ${Number(m)} minutes ${Number(s)} seconds to go`}
              >
                <div className="ww-countdown-unit">
                  <span className="ww-countdown-num">{h}</span>
                  <span className="ww-countdown-label">Hours</span>
                </div>
                <span className="ww-countdown-sep" aria-hidden="true">:</span>
                <div className="ww-countdown-unit">
                  <span className="ww-countdown-num">{m}</span>
                  <span className="ww-countdown-label">Min</span>
                </div>
                <span className="ww-countdown-sep" aria-hidden="true">:</span>
                <div className="ww-countdown-unit">
                  <span className="ww-countdown-num">{s}</span>
                  <span className="ww-countdown-label">Sec</span>
                </div>
              </div>
            );
          })()}
        </div>
      </PageShell>
    );
  }

  // ── LOADING ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <PageShell>
        <div className="ww-card-frame">
          <div className="ww-loader" role="status">
            <div className="ww-loader-ring" />
            <p className="ww-loader-label">Converge Checkout is loading...</p>
          </div>
        </div>
      </PageShell>
    );
  }

  // ── ERROR ─────────────────────────────────────────────────────────────
  if (error) {
    return (
      <PageShell>
        <div className="ww-card-frame">
          <div className="ww-error">
            <span className="ww-error-icon">◎</span>
            <h2>Something went wrong</h2>
            <p>{error}</p>
          </div>
        </div>
      </PageShell>
    );
  }

  // ── INTRO ─────────────────────────────────────────────────────────────
  if (phase === "intro") {
    return (
      <PageShell>
        <div className="ww-card-frame" ref={cardRef}>
          <img className="ww-card-bg" src={bg1} alt="" draggable="false" />
          <button type="button" className="ww-letsgo-btn" onClick={handleStart} aria-label="Let's go">
            <img src={btn1} alt="Let's go" draggable="false" />
          </button>
        </div>
      </PageShell>
    );
  }

  // ── Slide content per key ─────────────────────────────────────────────
  const renderSlideInner = (key) => {
    switch (key) {
      case "interested":
        return (
          <div className="ww-slide-inner ww-interested-inner">
            <div className="ww-interested-heading">
              A few sessions caught your 👀 today.
              <br />
              <strong>Which ones are you still thinking about?</strong>
            </div>
            <div className="ww-interested-list">
              {interestedSessions.map((s, i) => (
                <div className={`ww-interested-box ww-interested-box--${i + 1}`} key={i}>
                  {s.title}
                </div>
              ))}
            </div>
          </div>
        );

      case "personality":
        return (
          <div className="ww-slide-inner ww-personality-inner">
            <div className="ww-personality-headline">
              {data.firstName}, you were {personality.article}
              <br />
              <strong>{personality.title}</strong>
            </div>
            <div className="ww-personality-subhead">
              {personality.subhead.split("\n").map((line, i) =>
                line === "" ? (
                  <br key={i} />
                ) : (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                )
              )}
            </div>
          </div>
        );

      case "leaderboard":
        return (
          <div className="ww-slide-inner ww-leaderboard-inner">
            <div className="ww-leaderboard-headline">{leaderboardCopy.headline(data.firstName)}</div>
            <div className="ww-leaderboard-score">{data.totalScore}</div>
            <div className="ww-leaderboard-subhead">
              points puts you at Rank {data.rankNumber} on the Converge Leaderboard.
              {leaderboardCopy.subheadTail && (
                <>
                  {" "}
                  {leaderboardCopy.subheadTail.split("\n").map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
                </>
              )}
            </div>
          </div>
        );

      case "crowd":
        return (
          <div className="ww-slide-inner ww-rhythm-inner">
            <div className="ww-rhythm-heading">
              One agenda. Many rhythms.
              <br />
              <strong>This was yours.</strong>
            </div>
            <div className="ww-rhythm-box">
              {data.rhythm.map((event, i) => (
                <div className="ww-rhythm-row" key={i}>
                  <span className="ww-rhythm-time">{formatRhythmTime(event.time)}</span>
                  <span className="ww-rhythm-label">{event.label}</span>
                </div>
              ))}
            </div>
            <div className="ww-rhythm-footer">
              What moments made the
              <br />
              day memorable for you?
            </div>
          </div>
        );

      case "hero":
        return (
          <div className="ww-slide-inner ww-outro-inner">
            <div className="ww-outro-socials">
              <a
                className="ww-outro-social-row"
                href="https://www.linkedin.com/company/walmart-global-tech-india/"
                target="_blank"
                rel="noreferrer"
              >
                <span className="ww-outro-icon linkedin">
                  <LinkedInIcon />
                </span>
                <span>Walmart Global Tech India</span>
              </a>
              <a
                className="ww-outro-social-row"
                href="https://www.instagram.com/walmartglobaltechindia/"
                target="_blank"
                rel="noreferrer"
              >
                <span className="ww-outro-icon instagram">
                  <InstagramIcon />
                </span>
                <span>walmartglobaltechindia</span>
              </a>
            </div>
            <div className="ww-outro-seeyou">
              See you
              <br />
              next year!
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ── STORY (carousel) ──────────────────────────────────────────────────
  const hasMultipleSlides = totalSlides > 1;

  return (
    <PageShell>
      <div
        className={`ww-card-frame ww-card-frame--story${isDragging ? " is-dragging" : ""}`}
        ref={cardRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={resetDrag}
        role="region"
        aria-roledescription="carousel"
        aria-label="Your Converge Checkout"
      >
        <div
          className="ww-slides-track"
          style={{
            width: `${totalSlides * 100}%`,
            transform: `translateX(calc(-${(currentSlide * 100) / totalSlides}% + ${dragPx}px))`,
            transition: isDragging ? "none" : undefined,
          }}
        >
          {activeSlides.map((key, i) => (
            <div
              key={key}
              data-slide-index={i}
              className={`ww-slide ww-slide-${key}`}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${totalSlides}`}
              aria-hidden={currentSlide !== i}
              style={{
                width: `${100 / totalSlides}%`,
                backgroundImage: `url(${SLIDE_BACKGROUNDS[key]})`,
              }}
            >
              {renderSlideInner(key)}
            </div>
          ))}
        </div>

        {/* Invisible tap zones. They sit UNDER the slide content, which lets
            taps fall through to them except on real links (outro socials). */}
        {hasMultipleSlides && (
          <>
            <div className="ww-nav-zone ww-nav-zone--prev" onClick={onZoneTap("prev")} aria-hidden="true" />
            <div className="ww-nav-zone ww-nav-zone--next" onClick={onZoneTap("next")} aria-hidden="true" />
          </>
        )}

        {/* Edge glow that plays each time a slide appears (key remounts it). */}
        {hasMultipleSlides && !isDragging && (
          <div className="ww-tap-hints" key={`hint-${currentSlide}`} aria-hidden="true">
            {!isFirst && <span className="ww-tap-hint ww-tap-hint--prev" />}
            {!isLast && <span className="ww-tap-hint ww-tap-hint--next" />}
          </div>
        )}
      </div>

      {hasMultipleSlides && (
        <div className="ww-carousel-nav">
          <div className="ww-dots">
            {activeSlides.map((key, i) => (
              <button
                key={key}
                type="button"
                className={`ww-dot${currentSlide === i ? " is-active" : ""}`}
                onClick={() => goToSlide(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={currentSlide === i ? "true" : undefined}
              />
            ))}
          </div>
          <p className={`ww-swipe-hint${hasInteracted ? " is-hidden" : ""}`} aria-hidden={hasInteracted}>
            Tap the sides or swipe to explore
          </p>
        </div>
      )}

      {renderDownloadButton()}
    </PageShell>
  );
};

export default WalmartAnalysis;